import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSurveyRuntimeApi } from '../context/SurveyRuntimeApiContext';
import { getApiErrorMessage } from '../api/runtimeErrors';
import { useSurveyUiToast } from '../hooks/useSurveyUiToast';

export const QUESTION_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'rating', label: 'Rating' },
  { value: 'single', label: 'Single choice' },
  { value: 'multi', label: 'Multiple choice' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'emotion', label: 'Emotion' },
  { value: 'likert', label: 'Likert scale' },
];

export const TYPE_LABELS = Object.fromEntries(QUESTION_TYPES.map((t) => [t.value, t.label]));

// Types whose options are typed one-per-line in this dialog. For likert the
// "options" are the matrix's statement rows (scale defaults to 5-pt agreement;
// customizable later in the survey builder).
const CHOICE_TYPES = new Set(['single', 'multi', 'dropdown', 'likert']);

function blankDraft() {
  return { type: 'text', label: '', optionsRaw: '', scale10: false };
}

function draftToPayload(d) {
  const payload = { type: d.type, label: d.label.trim() };
  if (CHOICE_TYPES.has(d.type)) {
    payload.options = d.optionsRaw
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((label, i) => ({ key: `opt_${i + 1}`, label }));
  }
  if (d.type === 'rating' && d.scale10) payload.config = { scale: 10 };
  return payload;
}

function draftError(d) {
  if (!d.label.trim()) return 'Question text is required.';
  if (CHOICE_TYPES.has(d.type)) {
    const opts = d.optionsRaw.split('\n').map((s) => s.trim()).filter(Boolean);
    if (opts.length < 1) return 'Add at least one option (one per line).';
  }
  return null;
}

/**
 * Create a question group, or add questions to an existing one.
 *
 * Used by the Groups page and by the survey builder, so authors get the same
 * flow in both places. Questions come from the bank (existing) and/or are
 * written here (new) — nothing existing is ever modified.
 *
 * mode = 'create' → group name/description + questions
 * mode = 'add'    → questions only, appended to `group`
 */
export default function QuestionGroupEditorDialog({ open, onClose, onSaved, mode = 'create', group }) {
  const { apiFetch } = useSurveyRuntimeApi();
  const { showError } = useSurveyUiToast();
  const isAdd = mode === 'add';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [bank, setBank] = useState(null);
  const [picked, setPicked] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    setName('');
    setDescription('');
    setPicked([]);
    setDrafts([]);
    setNameError('');
    setBank(null);
    let cancelled = false;
    void (async () => {
      try {
        const data = await apiFetch('/admin/question-bank');
        if (!cancelled) setBank(data);
      } catch (e) {
        if (!cancelled) {
          setBank([]);
          showError(getApiErrorMessage(e, 'Failed to load the question bank.'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiFetch, open, showError]);

  // Questions already in the group can't be added twice.
  const alreadyIn = useMemo(
    () => new Set((group?.questions ?? []).map((q) => q.id)),
    [group],
  );
  const bankOptions = useMemo(
    () => (bank ?? []).filter((d) => !alreadyIn.has(d.id)),
    [bank, alreadyIn],
  );

  const totalToAdd = picked.length + drafts.length;

  function addDraft() {
    setDrafts((d) => [...d, blankDraft()]);
  }

  function patchDraft(idx, patch) {
    setDrafts((d) => d.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  }

  function removeDraft(idx) {
    setDrafts((d) => d.filter((_, i) => i !== idx));
  }

  async function save() {
    if (!isAdd && !name.trim()) {
      setNameError('Group name is required.');
      return;
    }
    for (const d of drafts) {
      const err = draftError(d);
      if (err) {
        showError(err);
        return;
      }
    }
    if (totalToAdd === 0) {
      showError(
        isAdd ? 'Select or write at least one question to add.' : 'Add at least one question to the group.',
      );
      return;
    }

    setSaving(true);
    try {
      const body = {
        question_definition_ids: picked.map((p) => p.id),
        new_questions: drafts.map(draftToPayload),
      };
      const saved = isAdd
        ? await apiFetch(`/admin/question-groups/${group.id}/questions`, {
            method: 'POST',
            body: JSON.stringify(body),
          })
        : await apiFetch('/admin/question-groups', {
            method: 'POST',
            body: JSON.stringify({
              name: name.trim(),
              description: description.trim() || null,
              ...body,
            }),
          });
      onSaved?.(saved);
      onClose();
    } catch (e) {
      showError(getApiErrorMessage(e, isAdd ? 'Failed to add questions.' : 'Failed to create the group.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isAdd ? `Add questions to “${group?.name ?? ''}”` : 'New question group'}
      </DialogTitle>
      <DialogContent dividers>
        {!isAdd && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
            <TextField
              label="Group name"
              size="small"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError('');
              }}
              error={Boolean(nameError)}
              helperText={nameError || 'Shown to every tenant as a question template.'}
              autoFocus
              disabled={saving}
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              label="Description (optional)"
              size="small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={saving}
              inputProps={{ maxLength: 500 }}
            />
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
          Use existing questions
        </Typography>
        <Autocomplete
          multiple
          options={bankOptions}
          loading={bank === null}
          disabled={saving}
          value={picked}
          onChange={(e, next) => setPicked(next)}
          getOptionLabel={(o) => o.label || ''}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.id}>
              <Checkbox size="small" checked={selected} sx={{ mr: 0.5, p: 0.5 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {option.label}
                </Typography>
              </Box>
              <Chip
                size="small"
                variant="outlined"
                label={TYPE_LABELS[option.type] || option.type}
                sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
              />
            </li>
          )}
          renderTags={(selected, getTagProps) =>
            selected.map((option, index) => (
              <Chip size="small" label={option.label} {...getTagProps({ index })} key={option.id} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={picked.length === 0 ? 'Search the question bank…' : undefined}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {bank === null ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Divider sx={{ my: 2.5 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Write new questions
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addDraft} disabled={saving} sx={{ textTransform: 'none' }}>
            Add question
          </Button>
        </Box>
        {drafts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            New questions are created in the shared question bank and added to this group.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {drafts.map((d, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: '8px' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    label={`Question ${idx + 1}`}
                    size="small"
                    fullWidth
                    value={d.label}
                    onChange={(e) => patchDraft(idx, { label: e.target.value })}
                    disabled={saving}
                    inputProps={{ maxLength: 1000 }}
                  />
                  <TextField
                    label="Type"
                    size="small"
                    select
                    value={d.type}
                    onChange={(e) => patchDraft(idx, { type: e.target.value })}
                    disabled={saving}
                    sx={{ minWidth: 150 }}
                  >
                    {QUESTION_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => removeDraft(idx)} disabled={saving}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {CHOICE_TYPES.has(d.type) && (
                  <TextField
                    label={d.type === 'likert' ? 'Statements (one per line)' : 'Options (one per line)'}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={d.optionsRaw}
                    onChange={(e) => patchDraft(idx, { optionsRaw: e.target.value })}
                    disabled={saving}
                    sx={{ mt: 1.5 }}
                  />
                )}
                {d.type === 'rating' && (
                  <FormControlLabel
                    sx={{ mt: 0.5 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={d.scale10}
                        onChange={(e) => patchDraft(idx, { scale10: e.target.checked })}
                        disabled={saving}
                      />
                    }
                    label={<Typography variant="body2">Use a 1–10 scale (default is 1–5)</Typography>}
                  />
                )}
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={saving || totalToAdd === 0}
          sx={{
            textTransform: 'none',
            backgroundColor: '#000000',
            '&:hover': { backgroundColor: '#333333' },
          }}
        >
          {saving
            ? 'Saving…'
            : isAdd
              ? `Add ${totalToAdd} question${totalToAdd === 1 ? '' : 's'}`
              : `Create group with ${totalToAdd} question${totalToAdd === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
